import { MessageType } from '../../../type';
import { Box, CardMedia, CircularProgress, IconButton } from '@mui/material';
import CancelIcon from '@mui/icons-material/Close';
import RetryIcon from '@mui/icons-material/Replay';
import { useUploadProgress } from '../../../hooks/store/useUploadProgress';
import { useCallback, useContext } from 'react';
import {
  cancelUpload,
  useResendFile,
} from '../../../hooks/firebase/useSendFiles';
import { MediaErrorMessageAwareContext } from '../by-side/MediaErrorMessageAware';

type LocalMediaMessageProps = {
  channelGuid: string;
  messageGuid: string;
  messageType: Extract<MessageType, 'VIDEO' | 'IMAGE'>;
  url?: string;
  fileName?: string;
};

export const LocalMediaMessage = (props: LocalMediaMessageProps) => {
  const { channelGuid, messageGuid, messageType, url, fileName } = props;
  const cancel = useCallback(
    () => cancelUpload(channelGuid, messageGuid),
    [channelGuid, messageGuid],
  );
  const resend = useResendFile(channelGuid, messageGuid);
  const uploadProgress = useUploadProgress(channelGuid, messageGuid);
  const [state, progress] = uploadProgress ?? ['running', 0];
  const component = messageType === 'IMAGE' ? 'img' : 'video';
  const errorAwareContext = useContext(MediaErrorMessageAwareContext);
  const onError = useCallback(() => {
    if (errorAwareContext) {
      errorAwareContext.onError();
    }
  }, [errorAwareContext]);
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <CardMedia
        sx={{ borderRadius: 3, maxWidth: 500 }}
        component={component}
        alt={fileName}
        image={url}
        onError={onError}
      />
      {state !== 'error' && (
        <>
          <Box sx={{ position: 'absolute', alignSelf: 'center' }}>
            <CircularProgress
              variant='determinate'
              sx={{ color: (theme) => theme.palette.grey[600] }}
              size={48}
              thickness={4}
              value={100}
            />
          </Box>
          <Box sx={{ position: 'absolute', alignSelf: 'center' }}>
            <CircularProgress
              size={48}
              variant='determinate'
              thickness={4}
              value={progress}
              sx={{ color: 'white' }}
            />
          </Box>
          <IconButton
            sx={{
              display: 'block',
              position: 'absolute',
              alignSelf: 'center',
              p: 3,
            }}
            onClick={cancel}
          >
            <CancelIcon sx={{ color: 'white' }} />
          </IconButton>
        </>
      )}
      {state === 'error' && (
        <IconButton
          sx={{ position: 'absolute', alignSelf: 'center', p: 3 }}
          onClick={resend}
        >
          <RetryIcon sx={{ color: 'white', width: 32, height: 32 }} />
        </IconButton>
      )}
    </Box>
  );
};
